import { useState } from "react";
import { toast } from "sonner";
import { Camera, Loader2 } from "lucide-react";
import { api, CATEGORIES, errMsg, img } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field, inputCls } from "@/components/landing/ProposalForm";

export const ProfileForm = ({ initial, onSave }) => {
  const [f, setF] = useState({
    name: initial.name || "", bio: initial.bio || "", city: initial.city || "", specialties: initial.specialties || [], instagram: initial.instagram || "",
    whatsapp: initial.whatsapp || "", booking_fee: (initial.booking_fee_cents ?? 0) / 100, listed: initial.listed ?? true,
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleSpec = (id) => setF({ ...f, specialties: f.specialties.includes(id) ? f.specialties.filter((s) => s !== id) : [...f.specialties, id] });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSave({ ...f, booking_fee: Number(f.booking_fee || 0) });
      toast.success("Perfil salvo");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="profile-form">
      <Field label="Nome público"><input required value={f.name} onChange={set("name")} className={inputCls} data-testid="profile-name-input" /></Field>
      <Field label="Cidade"><input value={f.city} onChange={set("city")} className={inputCls} data-testid="profile-city-input" /></Field>
      <Field label="Instagram"><input value={f.instagram} onChange={set("instagram")} placeholder="@seuperfil" className={inputCls} data-testid="profile-instagram-input" /></Field>
      <Field label="WhatsApp"><input value={f.whatsapp} onChange={set("whatsapp")} className={inputCls} data-testid="profile-whatsapp-input" /></Field>
      <Field label="Sinal para reservar data (R$) — 0 = só orçamento"><input type="number" min="0" step="10" value={f.booking_fee} onChange={set("booking_fee")} className={inputCls} data-testid="profile-fee-input" /></Field>
      <Field label="Aparecer na página de fotógrafos">
        <select value={f.listed ? "1" : "0"} onChange={(e) => setF({ ...f, listed: e.target.value === "1" })} className={inputCls} data-testid="profile-listed-select">
          <option value="1" className="bg-ink">Sim</option><option value="0" className="bg-ink">Não</option>
        </select>
      </Field>
      <div className="sm:col-span-2">
        <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-haze">Especialidades</span>
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button type="button" key={c.id} onClick={() => toggleSpec(c.id)} data-testid={`profile-spec-${c.id}`}
              className={`rounded-full border px-4 py-2 text-sm ${f.specialties.includes(c.id) ? "border-flash bg-flash text-ink" : "border-white/15"}`}>{c.label}</button>
          ))}
        </div>
      </div>
      <Field label="Bio" className="sm:col-span-2"><textarea rows={4} value={f.bio} onChange={set("bio")} className={inputCls} data-testid="profile-bio-input" /></Field>
      <button disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-full bg-flash px-6 py-3 font-semibold text-ink disabled:opacity-60 sm:justify-self-start" data-testid="profile-save-btn">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Salvar perfil
      </button>
    </form>
  );
};

export const AvatarUpload = ({ userId, hasAvatar, url, testid }) => {
  const [v, setV] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(url, fd);
      setV(Date.now());
      toast.success("Foto atualizada");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <label className="group relative block aspect-[4/5] w-40 cursor-pointer overflow-hidden rounded-2xl border border-white/10 bg-ink-3" data-testid={testid}>
      {(hasAvatar || v) && <img src={`${img("avatar", userId)}?v=${v}`} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
      <span className="absolute inset-0 grid place-items-center bg-ink/50 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100">
        {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <Camera className="h-6 w-6" />}
      </span>
      <input type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0])} />
    </label>
  );
};

export const PanelProfile = ({ user }) => {
  const { refresh } = useAuth();
  return (
    <div className="grid gap-8 lg:grid-cols-[180px_1fr]" data-testid="panel-profile">
      <AvatarUpload userId={user.id} hasAvatar={!!user.avatar_path} url="/panel/me/avatar" testid="profile-avatar-upload" />
      <ProfileForm initial={user} onSave={async (body) => { await api.patch("/panel/me", body); await refresh(); }} />
    </div>
  );
};
